import React, { forwardRef, useCallback, useId, useRef, useState } from 'react';
import { ChevronDown, Eye, EyeOff } from 'lucide-react';
import TextInput from './TextInput';
import Field, { inputClass, describedBy } from './Field';
import {
  formatUsDateInput,
  formatPhoneInput,
  formatSsnInput,
  formatZipInput,
  withMask
} from '../../../utils/dateInput';

const MONTH_OPTIONS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  .map((label, index) => ({ value: String(index + 1).padStart(2, '0'), label }));

const splitMonthValue = (value) => {
  const [month = '', year = ''] = String(value || '').split('/');
  return { month, year };
};

/**
 * Month + year as two dropdowns (native wheels on phones) instead of typing
 * "MM/YYYY". The form value stays the "MM/YYYY" string the API expects, and it
 * works with react-hook-form's register(): the registered ref is a hidden
 * input that holds the value, and changes are reported through onChange.
 * A half-picked value ("05/" or "/2021") is reported as-is so validation can
 * say what is missing.
 */
export const MonthInput = forwardRef(({
  name,
  onChange,
  onBlur,
  label,
  hint,
  error,
  required,
  optionalLabel,
  className,
  disabled = false,
  id: idProp,
  yearsBack = 70,
  yearsForward = 0
}, ref) => {
  const generatedId = useId();
  const id = idProp || `${name || 'month'}-${generatedId}`;
  const hiddenRef = useRef(null);
  const [parts, setParts] = useState({ month: '', year: '' });

  // react-hook-form writes the default value into the registered element when
  // the ref attaches; read it back so the dropdowns show saved answers.
  const setRefs = useCallback((element) => {
    hiddenRef.current = element;
    if (typeof ref === 'function') ref(element);
    else if (ref) ref.current = element;
    if (element) {
      const next = splitMonthValue(element.value);
      setParts((prev) => (prev.month === next.month && prev.year === next.year ? prev : next));
    }
  }, [ref]);

  const report = (next) => {
    setParts(next);
    const value = next.month || next.year ? `${next.month}/${next.year}` : '';
    if (hiddenRef.current) hiddenRef.current.value = value;
    onChange?.({ target: { name, value }, type: 'change' });
  };

  const currentYear = new Date().getFullYear();
  const years = [];
  for (let year = currentYear + yearsForward; year >= currentYear - yearsBack; year -= 1) years.push(String(year));
  if (parts.year && !years.includes(parts.year)) years.push(parts.year);

  // Tighter side padding so "Month" fits in narrow From/To columns.
  const selectClass = inputClass(Boolean(error), 'appearance-none pl-2.5 pr-7');
  const blur = () => onBlur?.({ target: { name, value: hiddenRef.current?.value || '' }, type: 'blur' });

  return (
    <Field id={id} label={label} hint={hint} error={error} required={required} optionalLabel={optionalLabel} className={className}>
      <input type="hidden" ref={setRefs} disabled={disabled} />
      <div className="grid grid-cols-2 gap-2">
        <div className="relative">
          <select
            id={id}
            data-field={name}
            value={parts.month}
            onChange={(e) => report({ ...parts, month: e.target.value })}
            onBlur={blur}
            disabled={disabled}
            aria-invalid={error ? 'true' : undefined}
            aria-describedby={describedBy(id, { hint, error })}
            aria-required={required || undefined}
            className={selectClass}
          >
            <option value="">Month</option>
            {MONTH_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
          <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden="true" />
        </div>
        <div className="relative">
          <select
            aria-label={`${label || 'Date'}: year`}
            value={parts.year}
            onChange={(e) => report({ ...parts, year: e.target.value })}
            onBlur={blur}
            disabled={disabled}
            aria-invalid={error ? 'true' : undefined}
            aria-describedby={describedBy(id, { hint, error })}
            aria-required={required || undefined}
            className={selectClass}
          >
            <option value="">Year</option>
            {years.map((year) => <option key={year} value={year}>{year}</option>)}
          </select>
          <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden="true" />
        </div>
      </div>
    </Field>
  );
});
MonthInput.displayName = 'MonthInput';

/** MM/DD/YYYY as masked text (use DateInput for the native picker). */
export const UsDateInput = forwardRef(({ onChange, placeholder = 'MM/DD/YYYY', ...rest }, ref) => (
  <TextInput
    ref={ref}
    inputMode="numeric"
    autoComplete="off"
    placeholder={placeholder}
    maxLength={10}
    onChange={withMask(formatUsDateInput, onChange)}
    {...rest}
  />
));
UsDateInput.displayName = 'UsDateInput';

/** Native date input; value is 'YYYY-MM-DD'. */
export const DateInput = forwardRef((props, ref) => (
  <TextInput ref={ref} type="date" inputClassName="min-h-[46px] sm:min-h-0" {...props} />
));
DateInput.displayName = 'DateInput';

/** (XXX) XXX-XXXX */
export const PhoneInput = forwardRef(({ onChange, placeholder = '(555) 555-5555', ...rest }, ref) => (
  <TextInput
    ref={ref}
    type="tel"
    inputMode="tel"
    autoComplete="tel"
    placeholder={placeholder}
    maxLength={14}
    onChange={withMask(formatPhoneInput, onChange)}
    {...rest}
  />
));
PhoneInput.displayName = 'PhoneInput';

/** 12345 or 12345-6789 */
export const ZipInput = forwardRef(({ onChange, placeholder = '12345', ...rest }, ref) => (
  <TextInput
    ref={ref}
    inputMode="numeric"
    autoComplete="postal-code"
    placeholder={placeholder}
    maxLength={10}
    onChange={withMask(formatZipInput, onChange)}
    {...rest}
  />
));
ZipInput.displayName = 'ZipInput';

/**
 * XXX-XX-XXXX with show/hide. Masked (password) by default so shoulder-surfers
 * on a phone do not see it. Never prefilled from the server; the server only
 * ever returns the last four digits.
 */
export const SsnInput = forwardRef(({ onChange, label = 'Social Security Number', last4, ...rest }, ref) => {
  const [visible, setVisible] = useState(false);

  return (
    <div className="relative">
      <TextInput
        ref={ref}
        label={label}
        type={visible ? 'text' : 'password'}
        inputMode="numeric"
        autoComplete="off"
        placeholder={last4 ? `On file: •••-••-${last4} (enter to change)` : '123-45-6789'}
        maxLength={11}
        inputClassName="pr-12"
        onChange={withMask(formatSsnInput, onChange)}
        {...rest}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        className="absolute right-2 top-[34px] sm:top-[31px] rounded p-1.5 text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        aria-label={visible ? 'Hide Social Security Number' : 'Show Social Security Number'}
      >
        {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
});
SsnInput.displayName = 'SsnInput';
